import React from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Global React Error Boundary — catches any render-time JS error,
 * POSTs the stack trace to /api/admin/error-logs/client, and shows a
 * friendly fallback. Admins can inspect captured errors at /admin/error-logs.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  async componentDidCatch(error, info) {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/admin/error-logs/client`,
        {
          message: String(error?.message || error || 'Unknown render error'),
          stack_trace: `${error?.stack || ''}\n---Component Stack---\n${info?.componentStack || ''}`,
          route: window.location.pathname,
          user_agent: navigator.userAgent,
        },
        token ? { headers: { Authorization: `Bearer ${token}` } } : {}
      );
    } catch (e) {
      // swallow — can't report errors while reporting errors
    }
  }

  reload = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          data-testid="error-boundary"
          className="min-h-screen flex items-center justify-center p-6 bg-background"
        >
          <div className="max-w-md w-full rounded-2xl border-2 border-red-500/40 bg-red-500/5 p-6 space-y-4 text-center">
            <div className="text-4xl">⚠️</div>
            <h1 className="text-xl font-bold uppercase tracking-wide">Something went wrong</h1>
            <p className="text-sm text-muted-foreground">
              The error has been logged and will be reviewed by an admin.
            </p>
            <pre className="text-xs font-mono text-left bg-background/50 rounded p-2 overflow-auto max-h-32 text-red-500">
              {String(this.state.error?.message || this.state.error || 'Unknown error')}
            </pre>
            <button
              onClick={this.reload}
              data-testid="error-boundary-reload-btn"
              className="inline-flex items-center justify-center h-11 px-6 rounded-full font-bold uppercase tracking-wider bg-primary text-primary-foreground hover:opacity-90"
            >
              Reload App
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
